/*
 *  Copyright (C) 2026 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { fromNullable, Option } from 'fp-ts/lib/Option';
import { getApiUrl, getRootUrl, getUserId } from 'sdi/app';
import { IViewEvent } from 'sdi/map';
import { assign, dispatch } from 'sdi/shape/functions';
import { IMapInfo } from 'sdi/source';
import { getLayerInfoList } from '../queries/app';
import { fetchLayers, fetchMap } from '../remote';
import { loadVisibleLayers } from './layer';
import { fitExtent, viewEvents } from './map';
import { parseRoute } from './route';

const fetchLayerInfos = (mapId: string) => {
    const url = `${getApiUrl(`layerinfos?mid=`)}${mapId}`;
    return fetchLayers(url).then(newLayers => {
        dispatch('data/layerinfo-list', layerList => {
            return layerList
                .filter(
                    layer => newLayers.findIndex(nl => nl.id == layer.id) < 0,
                )
                .concat(newLayers);
        });
        return getLayerInfoList();
    });
};

const fitMapExtent = (info: IMapInfo) =>
    fromNullable(info.extent).map(fitExtent);

const fetchMapById = (mid: string, view: Option<IViewEvent>) =>
    fetchMap(getApiUrl(`maps/${mid}`))
        .then(info => {
            assign('data/map', info);
            view.foldL<void>(
                () => fitMapExtent(info),
                viewEvents.updateMapView,
            );
            fetchLayerInfos(info.id).then(loadVisibleLayers);
        })
        .catch(response => {
            const user = getUserId();

            if (response.status === 403 && user.isNone()) {
                window.location.assign(getRootUrl(`login/embed/${mid}`));
                return;
            }
        });

export const loadMapFromRoute = () => {
    const { mapId, view } = parseRoute();
    return fromNullable(mapId)
        .map(mid => fetchMapById(mid, view))
        .getOrElseL(() => Promise.resolve(void 0)); // warning, it's rather ugly, but otherwise it breaks, so please keep it - pm
};

export const receiveMap = (info: IMapInfo) => {
    assign('data/map', info);
    fitMapExtent(info);
    loadVisibleLayers(getLayerInfoList());
};
